import * as React from "react";
import { Box, Card, Stack, Typography } from "@mui/material";

/**
 * The four bits of furniture every tab on this screen is built from.
 *
 * Deliberately thin. They exist so five tabs share one panel, one label/value pair and one muted
 * line rather than five slightly different ones — anything with behaviour or data in it belongs in
 * `../components`, which is shared with the wizard and the admin surfaces.
 *
 * Panels no longer take a subtitle. Every subtitle this screen used to carry was a sentence
 * explaining how the product works — "One per language, Singapore and Australia share an English
 * agent" — printed above content that already showed it. The title says what the panel is; the
 * content says what it holds.
 */

export interface PanelProps {
	title: string;
	/** Right-hand furniture on the title row: a status chip, a text button. Never a paragraph. */
	action?: React.ReactNode;
	children: React.ReactNode;
}

export function Panel({ title, action, children }: PanelProps): React.JSX.Element {
	return (
		// Fills its cell rather than shrinking to its content, so two panels sharing a row line up
		// at the bottom however much each of them holds. In a plain column this resolves to auto and
		// changes nothing.
		<Card variant="outlined" sx={{ display: "flex", flexDirection: "column", height: "100%", p: 2 }}>
			<Stack spacing={2} sx={{ flex: "1 1 auto" }}>
				<Stack
					direction="row"
					spacing={1}
					sx={{ alignItems: "center", justifyContent: "space-between", minHeight: 32 }}
				>
					<Typography variant="h6">{title}</Typography>
					{action}
				</Stack>

				{children}
			</Stack>
		</Card>
	);
}

/** One fact: what it is, and what it says. */
export function Field({ label, value }: { label: string; value: React.ReactNode }): React.JSX.Element {
	return (
		<Box sx={{ minWidth: 170 }}>
			<Typography variant="caption" color="text.secondary">
				{label}
			</Typography>
			<Typography variant="body2" component="div">
				{value}
			</Typography>
		</Box>
	);
}

/** A wrapping row of {@link Field}s. */
export function Facts({ children }: { children: React.ReactNode }): React.JSX.Element {
	return <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>{children}</Box>;
}

/**
 * The one line an empty panel is allowed.
 *
 * What used to sit here was a dashed box holding two or three sentences about a step that had not
 * happened yet — an essay about an absence, given the same weight as the content it stood in for.
 */
export function Muted({ children }: { children: React.ReactNode }): React.JSX.Element {
	return (
		<Typography variant="body2" color="text.secondary">
			{children}
		</Typography>
	);
}

/**
 * What an empty panel holds when it is standing beside a full one.
 *
 * Still one short line — the essay stays gone. What it gains is height and a shape: a bare
 * {@link Muted} line is one text row tall, so "What we capture" with nothing in it drew a card a
 * third the height of the outcomes card next to it and the pair read as a layout fault. This is a
 * deliberate placeholder rather than a collapsed panel.
 */
export function EmptyState({ children }: { children: React.ReactNode }): React.JSX.Element {
	return (
		<Box
			sx={{
				alignItems: "center",
				border: "1px dashed var(--mui-palette-divider)",
				borderRadius: 1,
				display: "flex",
				justifyContent: "center",
				minHeight: 104,
				px: 2,
				py: 3,
				textAlign: "center",
			}}
		>
			<Typography variant="body2" color="text.secondary">
				{children}
			</Typography>
		</Box>
	);
}

/**
 * One headline number.
 *
 * The note under the figure is one short line and is only there where the figure would be
 * misread without it — what is firm, and what is an estimate. It is not a hover: on the tiles a
 * client reads to find out what this costs, a hover is invisible to a scanning eye and to a touch
 * screen both.
 */
export function Tile({ label, value, note }: { label: string; value: string; note?: string }): React.JSX.Element {
	return (
		<Card variant="outlined" sx={{ height: "100%", p: 2 }}>
			<Typography variant="caption" color="text.secondary" sx={{ letterSpacing: ".06em", textTransform: "uppercase" }}>
				{label}
			</Typography>
			<Typography variant="h6" sx={{ mt: 0.5 }}>
				{value}
			</Typography>
			{note ? (
				<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
					{note}
				</Typography>
			) : null}
		</Card>
	);
}
